import { Location } from '@cobliteam/common-types';
import useChecklistNonComplianceAlertSuggestion from '@cobliteam/hooks/src/useAlertHooks/useChecklistNonComplianceAlertSuggestion';
import { useNudge } from '@cobliteam/hooks/src/useNudge';
import type { Nudge, NudgeSurface } from '@cobliteam/hooks/src/useNudge/types';
import { useTranslation } from '@cobliteam/i18n';
import NudgeMessage from '@cobliteam/ui-business/components/Nudge/NudgeMessage';
import { useLocation } from 'react-router-dom';

export const CHECKLIST_NON_COMPLIANCE_ALERT_NUDGE_ID = 'checklist-non-compliance-alert';

/**
 * Card of the non-compliance alert (variant C of the design). Eligibility is the same as the banner's
 * on the checklists page, where the card stays out so the two never show together. "Não tenho
 * interesse" records the alert-hook dismissal, which retires the banner too.
 */
export const useChecklistNonComplianceAlertNudge = (surface: NudgeSurface = 'floating-card'): Nudge | null => {
  const { t } = useTranslation('alertHooks');
  const { pathname } = useLocation();
  const suggestion = useChecklistNonComplianceAlertSuggestion();
  const isOnChecklistPages = pathname.startsWith(Location.CHECKLIST);

  const { shouldShow, dismiss, accept } = useNudge({
    id: CHECKLIST_NON_COMPLIANCE_ALERT_NUDGE_ID,
    kind: 'recommendation',
    surface,
    useShouldShow: () => suggestion.isEligible && !isOnChecklistPages,
  });

  if (!shouldShow) return null;

  const notInterested = (reason?: string) => {
    suggestion.dismiss();
    dismiss(reason);
  };

  const title = t('alertHooks:CHECKLIST_NON_COMPLIANCE.nudge.title');

  return {
    id: CHECKLIST_NON_COMPLIANCE_ALERT_NUDGE_ID,
    kind: 'recommendation',
    priority: 2,
    tagLabel: t('alertHooks:CHECKLIST_NON_COMPLIANCE.nudge.tagLabel'),
    title,
    dismiss: notInterested,
    render: () => (
      <NudgeMessage
        title={title}
        description={t('alertHooks:CHECKLIST_NON_COMPLIANCE.nudge.description')}
        ctaLabel={t('alertHooks:CHECKLIST_NON_COMPLIANCE.nudge.cta')}
        onCta={() => {
          accept({ alert_type: 'checklist-non-compliance' });
          void suggestion.activate();
        }}
        dismissLabel={t('alertHooks:CHECKLIST_NON_COMPLIANCE.nudge.dismiss')}
        onDismiss={() => notInterested('not-interested')}
      />
    ),
  };
};

export default useChecklistNonComplianceAlertNudge;
